import { useAppDispatch } from 'app/hooks';
import { FETCH_ALL_PARAMS } from 'constants/query';
import { useForm, ValidationErrorsType, ValidationErrorType } from 'hooks/useForm';
import { useRole } from 'hooks/useRole';
import { useWorkspace } from 'hooks/useWorkspace';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAddPromotionMutation } from '../services';
import { useGetPlacesQuery } from 'features/place/services';
import { useGetProductsWithJoinQuery } from 'features/product/services';
import { PROMOTION_FIELDS_SCHEMA, PROMOTION_INITIAL_FIELDS } from '../constants/promotion-fields';
import { removeEmpty, trimValues } from 'utils/object-helpers';
import { PromotionDto } from '../types/dtos';
import { ServerErrorType } from 'types/server-responses';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { SelectChangeEvent } from '@mui/material';
import { validateConfigFields } from 'utils/products-config-validation-helper';
import { PROMOTION_TYPE } from '../constants/type';

export type AllErrorsType<T> = ValidationErrorsType<T> & {
    [key: string]: ValidationErrorType | undefined;
};

export type ProductConfigData = {
    productId: string;
    discountedPrice?: string;
    name?: string;
    price?: string;
};

const useController = () => {
    const { placeId = '' } = useParams();
    const [productConfigData, setProductConfigData] = useState<ProductConfigData[]>([]);
    const [placeIds, setPlaceIds] = useState<string[]>([]);
    const [priceErrors, setPriceErrors] = useState<Record<string, ValidationErrorType>>({});
    const { isAdmin } = useRole();
    const { websiteId } = useWorkspace();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [addPromotion, { isLoading: isAddPromotionLoading }] = useAddPromotionMutation();
    const { data: places, isLoading: isGetPlacesLoading } = useGetPlacesQuery({
        ...FETCH_ALL_PARAMS,
        ids: placeId,
    });
    const { data: products, isLoading: isGetProductsLoading } = useGetProductsWithJoinQuery({
        ...FETCH_ALL_PARAMS,
        placeId: placeId,
    });

    const {
        formData,
        handleChange: onChange,
        handleSubmit,
        errors,
    } = useForm({ defaultValues: PROMOTION_INITIAL_FIELDS, validationSchema: PROMOTION_FIELDS_SCHEMA });

    const validatePrices = useCallback(
        (configs: ProductConfigData[]): Record<string, ValidationErrorType> => {
            const isDiscountPromotion = formData.type === PROMOTION_TYPE.DISCOUNTED;

            return validateConfigFields<ProductConfigData>(configs, 'productId', ['discountedPrice'], {
                discountedPrice: {
                    required: isDiscountPromotion
                        ? {
                              message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_REQUIRED'),
                          }
                        : undefined,
                    isNumber: {
                        message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_NUMBER'),
                    },
                    isPositive: {
                        message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_POSITIVE'),
                    },
                    custom: isDiscountPromotion
                        ? [
                              {
                                  validator: (value, allValues) => {
                                      if (!value || !allValues.price) return true;
                                      const discounted = parseFloat(value);
                                      const price = parseFloat(allValues.price);
                                      return discounted < price;
                                  },
                                  message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_LOWER_THAN_PRICE'),
                              },
                          ]
                        : undefined,
                },
            });
        },
        [formData.type, t],
    );
    useEffect(() => {
        if (formData.type !== PROMOTION_TYPE.REGULAR) {
            setPriceErrors((prev) => {
                return { ...prev, ...validatePrices(productConfigData) };
            });
            return;
        } else {
            setPriceErrors({});
        }

        if (places?.content && placeId) {
            const initialPlace = places.content.find((place) => place.id === placeId);
            if (initialPlace) {
                setPlaceIds([placeId]);
            }
        }
    }, [places, placeId, formData.type, productConfigData, validatePrices]);

    const handlePlaceIdsSelectChange = useCallback(
        (event: SelectChangeEvent<string[]>) => {
            const {
                target: { value },
            } = event;
            const selectedIds = typeof value === 'string' ? value.split(',') : value;

            if (places?.content) {
                const newPlaceIds = places.content
                    .filter((place) => selectedIds.includes(place.id))
                    .map((place) => {
                        const existing = placeIds.find((id) => id === place.id);
                        return existing || place.id;
                    });

                setPlaceIds(newPlaceIds);
            }
        },
        [places?.content, placeIds, setPlaceIds],
    );

    const handleProductSelectChange = useCallback(
        (event: SelectChangeEvent<string[]>) => {
            const {
                target: { value },
            } = event;
            const selectedIds = typeof value === 'string' ? value.split(',') : value;

            if (selectedIds.length === 0) {
                setProductConfigData([]);
                return;
            }

            if (products?.content) {
                const removedProductIds = productConfigData
                    .map((config) => config.productId)
                    .filter((id) => !selectedIds.includes(id));

                const newProductConfigData = products.content
                    .filter((product) => selectedIds.includes(product.id))
                    .map((product) => ({
                        productId: product.id,
                        name: product.name,
                        price: product.price,
                        discountedPrice: product.discountedPrice?.split(' ')[0],
                    }));

                const newErrors = validatePrices(newProductConfigData);

                setPriceErrors((prevErrors) => {
                    const cleanedErrors = { ...prevErrors };
                    removedProductIds.forEach((id) => {
                        Object.keys(cleanedErrors)
                            .filter((key) => key.includes(`-${id}`))
                            .forEach((errorKey) => {
                                delete cleanedErrors[errorKey];
                            });
                    });
                    return {
                        ...cleanedErrors,
                        ...newErrors,
                    };
                });

                setProductConfigData(newProductConfigData);
            }
        },
        [products?.content, productConfigData, setProductConfigData, setPriceErrors, validatePrices],
    );

    const allErrors: AllErrorsType<typeof PROMOTION_INITIAL_FIELDS> = useMemo(() => {
        return {
            ...errors,
            ...priceErrors,
        };
    }, [errors, priceErrors]);

    const handleProductConfigPriceChange = useCallback(
        (field: string, value: string, errorPrefixes: string[], productId?: string) => {
            const updatedData = productId
                ? productConfigData.map((config) =>
                      config.productId === productId ? { ...config, [field]: value } : config,
                  )
                : productConfigData.map((config) => ({ ...config, [field]: value }));

            const newErrors = validatePrices(updatedData);

            setPriceErrors((errors) => ({
                ...Object.fromEntries(
                    Object.entries(errors).filter(([key]) => !errorPrefixes.some((prefix) => key.startsWith(prefix))),
                ),
                ...newErrors,
            }));

            setProductConfigData(updatedData);
        },
        [productConfigData, setProductConfigData, setPriceErrors, validatePrices],
    );

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'promotion.PROMOTION_ADDED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(() => {
        const data = { ...formData };
        data.placeId = placeId;
        data.importance = Number(data.importance);
        data.products = productConfigData.map((config) => {
            return {
                productId: config.productId,
                ...(config.discountedPrice !== undefined &&
                    data.type === PROMOTION_TYPE.DISCOUNTED && { discountedPrice: Number(config.discountedPrice) }),
            };
        });
        if (data.type === PROMOTION_TYPE.REGULAR) {
            delete data.startDate;
            delete data.endDate;
        }

        return trimValues(removeEmpty(data)) as PromotionDto;
    }, [formData, productConfigData, placeId]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                try {
                    const payload = getPayload();

                    await addPromotion(payload).unwrap();

                    handleSuccess();
                    navigate(-1);
                } catch (error) {
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [getPayload, handleErrors, addPromotion, handleSubmit, handleSuccess, navigate],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        formData,
        places,
        products,
        onSubmit,
        onCancel,
        onChange,
        isLoading: isGetPlacesLoading || isGetProductsLoading,
        isAddPromotionLoading: isAddPromotionLoading,
        errors: allErrors,
        isAdmin,
        websiteId,
        placeId,
        placeIds,
        handlePlaceIdsSelectChange,
        handleProductSelectChange,
        productConfigData,
        handleProductConfigPriceChange,
    };
};
export default useController;
